import { BufferGeometry } from 'three/src/core/BufferGeometry';
import { BufferAttribute } from 'three/src/core/BufferAttribute';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';

/**
 * Positions closer together than this are treated as the same vertex. Stope solids are authored in
 * metres, so 0.1 mm is far below any real feature yet comfortably above the round-tripping noise of
 * the OBJ text the importer writes.
 */
export const WELD_TOLERANCE = 1e-4;

export interface RepairStats {
  trianglesBefore: number;
  trianglesAfter: number;
  verticesBefore: number;
  verticesAfter: number;
}

const readTriangleIndices = (geometry: BufferGeometry, triangleCount: number): number[][] => {
  const { index } = geometry;
  return Array.from({ length: triangleCount }, (unused, triangle) => {
    const base = triangle * 3;
    return index
      ? [index.getX(base), index.getX(base + 1), index.getX(base + 2)]
      : [base, base + 1, base + 2];
  });
};

const isDegenerate = (position: BufferAttribute, corners: number[]): boolean => {
  const [a, b, c] = corners;
  if (a === b || b === c || c === a) return true;

  const coincident = (i: number, j: number): boolean => Math.abs(position.getX(i) - position.getX(j)) <= WELD_TOLERANCE
    && Math.abs(position.getY(i) - position.getY(j)) <= WELD_TOLERANCE
    && Math.abs(position.getZ(i) - position.getZ(j)) <= WELD_TOLERANCE;

  return coincident(a, b) || coincident(b, c) || coincident(c, a);
};

const withoutDegenerateFaces = (geometry: BufferGeometry, keptTriangles: number[][]): BufferGeometry => {
  const repaired = geometry.clone();
  repaired.setIndex(keptTriangles.flat());
  return repaired;
};

/**
 * Repairs the malformed stope solids the DUF importer has been writing: Deswik stores triangles as
 * four-vertex faces whose last vertex repeats the first, and the naive fan triangulation turns each
 * of those into one real triangle plus one zero-area triangle. Dropping the zero-area faces and then
 * welding coincident positions recovers a closed manifold (boundary edges 0, Euler characteristic 2)
 * with identical volume and half the triangle count.
 *
 * ORDER MATTERS. `mergeVertices` hashes EVERY attribute, so a mesh that already carries per-face
 * normals (OBJLoader always emits some) has a unique normal per corner and nothing merges. The normal
 * and uv attributes are therefore stripped first, and normals are deliberately NOT recomputed here -
 * the caller does that after its transforms.
 */
export const repairStopeGeometryWithStats = (geometry: BufferGeometry): { geometry: BufferGeometry; stats: RepairStats } => {
  const position = geometry.getAttribute('position') as BufferAttribute | undefined;
  const trianglesBefore = geometry.index ? geometry.index.count / 3 : (position?.count ?? 0) / 3;
  const verticesBefore = position?.count ?? 0;

  const emptyStats: RepairStats = {
    trianglesBefore,
    trianglesAfter: trianglesBefore,
    verticesBefore,
    verticesAfter: verticesBefore,
  };

  if (!position) return { geometry, stats: emptyStats };

  const stripped = geometry.clone();
  stripped.deleteAttribute('normal');
  stripped.deleteAttribute('uv');

  const keptTriangles = readTriangleIndices(stripped, trianglesBefore)
    .filter((corners) => !isDegenerate(position, corners));

  const welded = mergeVertices(withoutDegenerateFaces(stripped, keptTriangles), WELD_TOLERANCE);
  const weldedPosition = welded.getAttribute('position') as BufferAttribute | undefined;

  return {
    geometry: welded,
    stats: {
      trianglesBefore,
      trianglesAfter: welded.index ? welded.index.count / 3 : (weldedPosition?.count ?? 0) / 3,
      verticesBefore,
      verticesAfter: weldedPosition?.count ?? 0,
    },
  };
};

export const repairStopeGeometry = (geometry: BufferGeometry): BufferGeometry => repairStopeGeometryWithStats(geometry).geometry;
